import { createStore, type StoreApi } from "./store";
import { createVideoClockSync, type VideoClockEstimate } from "./video-clock";

export interface SentFrameTiming {
  rtpTimestamp: number;
  firstSendMs: number;
  lastSendMs: number;
}

export interface CameraVideoStats {
  mount: string;
  slot: number;
  active: boolean;
  sourceVersion: number;
  queueUnit: "frames" | "packets";
  queueItems: number | null;
  oldestWaitMs: number | null;
  meanWaitMs: number | null;
  maxWaitMs: number | null;
  receivedAt: number;
  producerId?: string | null;
}

export interface CameraFrameTiming {
  producerId: string | null;
  sentFrames: SentFrameTiming[];
}

interface Snapshot {
  byMount: Record<string, CameraVideoStats>;
  clock: VideoClockEstimate | null;
}
export interface CameraVideoStatsSlice extends Pick<
  StoreApi<Snapshot>,
  "getState" | "getInitialState" | "subscribe"
> {
  ingest(message: unknown): void;
  reset(): void;
  startClockSync(send: (text: string) => boolean | void): void;
  stopClockSync(): void;
  requestFrameTiming(mount: string): void;
  getFrameTiming(mount: string): CameraFrameTiming | undefined;
}

function nonnegative(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

export function createCameraVideoStatsSlice(now: () => number = Date.now): CameraVideoStatsSlice {
  const store = createStore<Snapshot>(() => ({ byMount: {}, clock: null }));
  const clock = createVideoClockSync((estimate) => store.setState({ clock: estimate }));
  let sendDiagnostic: ((text: string) => boolean | void) | null = null;
  // Diagnostic frame history is read on demand, not propagated through React props.
  let frameTiming: Record<string, CameraFrameTiming> = {};
  return {
    getState: store.getState,
    getInitialState: store.getInitialState,
    subscribe: store.subscribe,
    ingest(message) {
      let text = message;
      if (text instanceof ArrayBuffer) text = new TextDecoder().decode(new Uint8Array(text));
      else if (ArrayBuffer.isView(text)) text = new TextDecoder().decode(text);
      if (typeof text !== "string") return;
      try {
        const msg = JSON.parse(text);
        if (msg?.event === "video-clock-pong" && msg.payload && typeof msg.payload === "object") {
          clock.ingest(msg.payload);
          return;
        }
        if (msg?.event !== "camera-video-stats" || !Array.isArray(msg.payload?.cameras)) return;
        const entries: [string, CameraVideoStats][] = [];
        const nextTiming: Record<string, CameraFrameTiming> = {};
        for (const c of msg.payload.cameras) {
          if (
            !c ||
            typeof c.mount !== "string" ||
            !c.mount ||
            !Number.isInteger(c.slot) ||
            c.slot < 0 ||
            typeof c.active !== "boolean" ||
            !Number.isInteger(c.source_version) ||
            c.source_version < 0 ||
            (c.queue_unit !== "frames" && c.queue_unit !== "packets")
          )
            continue;
          const frames: SentFrameTiming[] = [];
          if (typeof c.producer_id === "string" && Array.isArray(c.sent_frames)) {
            for (const f of c.sent_frames.slice(-90)) {
              if (
                f &&
                Number.isInteger(f.rtp_timestamp) &&
                f.rtp_timestamp >= 0 &&
                f.rtp_timestamp <= 0xffffffff &&
                nonnegative(f.first_send_ms) !== null &&
                nonnegative(f.last_send_ms) !== null &&
                f.last_send_ms >= f.first_send_ms
              ) {
                frames.push({
                  rtpTimestamp: f.rtp_timestamp,
                  firstSendMs: f.first_send_ms,
                  lastSendMs: f.last_send_ms,
                });
              }
            }
          }
          nextTiming[c.mount] = {
            producerId: typeof c.producer_id === "string" ? c.producer_id : null,
            sentFrames: frames,
          };
          entries.push([
            c.mount,
            {
              mount: c.mount,
              slot: c.slot,
              active: c.active,
              sourceVersion: c.source_version,
              queueUnit: c.queue_unit,
              queueItems: Number.isInteger(c.queue_items) ? nonnegative(c.queue_items) : null,
              oldestWaitMs: nonnegative(c.oldest_wait_ms),
              meanWaitMs: nonnegative(c.mean_wait_ms),
              maxWaitMs: nonnegative(c.max_wait_ms),
              receivedAt: now(),
              producerId: typeof c.producer_id === "string" ? c.producer_id : null,
            },
          ]);
        }
        frameTiming = nextTiming;
        store.setState({ byMount: Object.fromEntries(entries) });
      } catch {
        /* Ignore unrelated/malformed DataChannel messages. */
      }
    },
    reset() {
      sendDiagnostic = null;
      frameTiming = {};
      clock.stop();
      store.setState({ byMount: {}, clock: null }, true);
    },
    startClockSync(send) {
      sendDiagnostic = send;
      clock.start(send);
    },
    stopClockSync() {
      sendDiagnostic = null;
      clock.stop();
    },
    requestFrameTiming(mount) {
      sendDiagnostic?.(JSON.stringify({ event: "video-frame-probe", payload: { mount } }));
    },
    getFrameTiming: (mount) => frameTiming[mount],
  };
}
